/**
 * Sevens bot.
 *
 * Sevens is mostly luck, but the order of play matters. For each playable card the bot looks at the position after
 * playing it and prefers the card that:
 *  - leaves it the most other playable cards (so it is never stuck waiting for the others),
 *  - lets it run straight on with cards it already holds in that suit (a chain),
 *  - works outward from the seven (cards far from the seven are played first, which opens less for the others),
 *  - and holds back sevens, since an unplayed seven blocks its whole suit.
 * With two cards left it plays whatever gets it out fastest. Weaker difficulties sometimes play at random.
 * The bot only ever reads its own seat's view.
 */
import { parseCard, type CardId, type Suit } from '../../core/cards.js';
import { chance, type BotEngine } from '../../bots/types.js';
import { sevensLegal, sevensPlayable, sevensRank, type SevensAction, type SevensLegal, type SevensRun, type SevensView } from './engine.js';

type Board = Record<Suit, SevensRun>;

/** The board after `card` is played. */
function after(board: Board, card: CardId): Board {
  const { suit } = parseCard(card);
  const r = sevensRank(card);
  const run = board[suit];
  return { ...board, [suit]: run === null ? { low: r, high: r } : { low: Math.min(run.low, r), high: Math.max(run.high, r) } };
}

function pickCard(view: SevensView, cards: CardId[]): CardId {
  let best = cards[0]!;
  let bestScore = -Infinity;
  for (const c of cards) {
    const rest = view.hand.filter((x) => x !== c);
    const next = after(view.board, c);
    const flexibility = rest.filter((x) => sevensPlayable(x, next)).length;
    const { suit } = parseCard(c);
    const r = sevensRank(c);
    const run = view.board[suit];
    // Cards that follow this one straight on in the same direction.
    let chain = 0;
    if (run !== null) {
      const dir = r > run.high ? 1 : -1;
      for (let k = r + dir; k >= 1 && k <= 13 && rest.some((x) => parseCard(x).suit === suit && sevensRank(x) === k); k += dir) chain++;
    }
    let score = flexibility + 0.7 * chain + 0.05 * Math.abs(r - 7);
    if (r === 7) score -= 1.5;
    if (view.hand.length <= 2) score += 3;
    if (score > bestScore) {
      bestScore = score;
      best = c;
    }
  }
  return best;
}

export const sevensBot: BotEngine<SevensView, SevensLegal, SevensAction> = {
  decideAction(view, legal, ctx) {
    return this.chooseCard!(view, legal, ctx);
  },

  chooseCard(view, legal, ctx) {
    if (legal.cards.length === 0) return { type: 'pass' };
    if (legal.cards.length === 1) return { type: 'play', card: legal.cards[0]! };
    if (chance(ctx, ctx.profile.mistakeRate)) return { type: 'play', card: legal.cards[ctx.rng.int(legal.cards.length)]! };
    return { type: 'play', card: pickCard(view, legal.cards) };
  },

  evaluateState(view) {
    if (view.seat === null) return 0;
    const mine = view.handCounts[view.seat]!;
    const others = view.handCounts.filter((_, s) => s !== view.seat);
    const avg = others.reduce((a, b) => a + b, 0) / others.length;
    return Math.max(0, Math.min(1, 0.5 + (avg - mine) / 26));
  },

  calculateRisk(view) {
    return view.seat === null ? 0 : Math.min(1, view.handCounts[view.seat]! / 13);
  },

  handleTimeout(view, legal) {
    const l = legal.cards.length > 0 ? legal : sevensLegal(view.hand, view.board, false);
    if (l.cards.length === 0) return { type: 'pass' };
    return { type: 'play', card: l.cards[0]! };
  },
};
